// Canvas 2D（PDF 出力・エクスポート画像）向けの Markdown 行レイアウト計算。
//
// 画面表示（MarkdownTextView / markdown-text.ts）と同じパース結果・スタイル定数を使い、
// 見出しの倍率 + 太字、箇条書き・番号付きリストのぶら下げインデント + 階層インデント + マーカー + 項目間隔、
// 引用の左罫と本文位置、水平線の横罫を Canvas の描画座標へ変換する。
// 行送りは画面の line-height（1.5）と同じ比率（STICKY_LINE_HEIGHT_RATIO）を使う。
// テキスト以外の塗り（水平線・引用の縦罫・表の外枠 / 列ヘッダー背景 / 列ヘッダーの区切り線・
// コードブロックの面と枠線・callout の背景と左罫）は Result.rects として返し、呼び出し側
// （PDF・サムネイル）が色を解決して描く。callout だけは type ごとに色が変わるため、
// レイアウトが解決済みの色を rect.fill へ載せる。
// 本設計の記法を含まない本文では rects が空で、既存の出力は 1px も変わらない。
// 表（GFM 基本形）は domain のブロック層（sourceMap.blocks）で認識し、列幅は画面（colgroup）と同じ
// 純粋関数・同じ式（markdownTableAvailableEm）で決めるため、画面と出力の列幅が一致する。
// コードブロック（``` フェンス）も同じブロック層で認識し、中身は記法を解釈せず生の行を等幅で積む。
//
// 文字幅の計測は関数注入（measure）とし、Canvas 2D の無い環境（jsdom のユニットテスト）
// でもフェイク計測で決定的に検証できるようにする。

import {
  buildMarkdownSourceMap,
  buildTextInlineDisplaySpans,
  hasMarkdownInlineDecorations,
  markdownCalloutHasTitle,
  markdownCalloutTitleDisplayText,
  markdownTableAvailableEm,
  markdownTableColumnWidthsEm,
  tableColumnWidthsAt,
  TABLE_BORDER_PX,
  TABLE_CELL_PADDING_EM,
  TABLE_ROW_MIN_HEIGHT_EM,
  type MarkdownCallout,
  type MarkdownCodeBlock,
  type MarkdownInlineDecoration,
  type MarkdownSourceMap,
  type MarkdownTable,
  type MarkdownTableAlign,
  type MarkdownTableCell,
  type TableColumnWidths,
  type TableColumnWidthsList,
  type TextInlineDisplaySpan,
  type TextInlineRawPiece,
  type TextInlineStyle,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import {
  BULLET_INDENT_EM,
  BULLET_ITEM_GAP_EM,
  CALLOUT_ACCENT_EM,
  CALLOUT_ACCENT_MIN_PX,
  CALLOUT_COLORS,
  CALLOUT_MARGIN_Y_EM,
  CALLOUT_PAD_X_EM,
  CALLOUT_PAD_Y_EM,
  CALLOUT_TITLE_GAP_EM,
  HEADING_FONT_SCALE,
  isMarkdownListLineKind,
  markdownCalloutTitleSpans,
  markdownCodeBlockLines,
  markdownLineBodyDisplayTextFor,
  markdownListMarkerTextFor,
  markdownListMarkerWidthEm,
  markdownTableCellSpans,
  markdownTextLineFromParts,
  MARKDOWN_CODE_BORDER_PX,
  MARKDOWN_CODE_PAD_X_EM,
  MARKDOWN_CODE_PAD_Y_EM,
  MARKDOWN_QUOTE_BAR_EM,
  MARKDOWN_QUOTE_INDENT_EM,
  MARKDOWN_RULE_THICKNESS_EM,
  MARKDOWN_TABLE_BODY_BACKGROUND_COLOR,
  MARKDOWN_TABLE_HEADER_TEXT_COLOR,
  MARKDOWN_TABLE_ROW_STRIPE_COLOR,
  MARKDOWN_TABLE_ROW_STRIPE_COLOR_ON_DARK,
  TASK_BOX_EM,
  type MarkdownTextLine,
} from "./markdown-text";
import { resolveTextBackground } from "./text-color";
import { isValidHexColor, normalizeHexColor } from "./hex-color";
import { STICKY_LINE_HEIGHT_RATIO } from "./sticky-font-fit";
import { INLINE_CODE_BACKGROUND_COLOR, INLINE_CODE_PADDING_EM } from "./text-inline-style-css";
import {
  resolveMarkdownTableForeground,
  TEXT_LINK_COLOR_PAIRS,
  type MarkdownTableBodyPalette,
} from "./text-link-color";

export declare namespace layoutMarkdownTextForCanvas {
  export type { Anchor, LayoutRect, Measure, MeasureFont, Options, Result, Segment };
}

/**
 * 本文テキストを Canvas 描画用のセグメント列へレイアウトする。
 *
 * - 見出し行: フォント = 基準 × HEADING_FONT_SCALE、太字。アンカーは align に従う。
 * - 箇条書き行・番号付きリスト行: マーカー / 項番付きテキストをぶら下げインデントで折り返す。
 *   アンカーは align に依らず常に左（インデント構造が中央 / 右寄せと両立しないため。画面表示と同じ仕様）。
 * - 引用行: 本文を左罫ぶん（MARKDOWN_QUOTE_INDENT_EM）右から始め、その幅で折り返す。左罫は
 *   `rects`（role "quote-bar"）として、折り返して占めた高さの合計で積む。本文の文字色は固定せず、
 *   DOM の `.canvas-md-quote`（currentColor）と同じく呼び出し側の本文色に委ねる。
 * - 水平線行: セグメントを出さず、1 行分の高さの中央へ横罫を `rects`（role "rule"）として積む。
 * - plain 行: 基準フォント。アンカーは align に従う。
 * - 空行: セグメントを出さず、基準フォント 1 行分の高さだけ送る。
 * - 表: 列幅を画面と同じ共有関数で決め、セルごとに本文を折り返し、外枠・列ヘッダー背景・
 *   列ヘッダーの区切り線を `rects` として積む（layoutTableBlock）。align には依らず常に左端から描く。
 * - コードブロック: フェンス行を出さず、中身の行を記法解釈なしの生テキストとして等幅で折り返し、
 *   面と枠線を `rects` として積む（layoutCodeBlock）。align には依らず常に内側の余白から左詰めで描く。
 * - callout: フェンス行を出さず、タイトルと本文行を内側の描画領域へ積み、背景と左罫を `rects` として
 *   積む（layoutCalloutBlock）。本文行の寄せ・インデント・折り返しは callout の外と同じ規則で、
 *   基準にする幅と原点だけが内側領域になる。
 *
 * 折り返しは既存の PDF 出力（旧 wrapTextToLines）と同じ 1 文字単位の詰め込み方式で、
 * 1 文字も入らない幅でもその 1 文字は必ず出力する（無限ループ防止）。その場合は
 * overflowsWidth を true にし、付箋のフォント自動フィットが「収まらない」と判定できる
 * ようにする。
 */
export function layoutMarkdownTextForCanvas(options: Options): Result {
  // 部分書式またはインライン装飾がある場合は、行内を 1 文字ずつフォントを切り替えて
  // レイアウトする専用経路へ分ける。どちらも無い既存本文では従来の経路をそのまま通し、
  // 出力を 1 バイトも変えない。
  if ((options.inlineStyles?.length ?? 0) > 0 || hasMarkdownInlineDecorations(options.text)) {
    return layoutMarkdownBlocks(options, layoutStyledLine, layoutStyledCalloutTitle);
  }
  return layoutMarkdownBlocks(options, layoutPlainLine, layoutPlainCalloutTitle);
}

/**
 * ブロック記法を持たない 1 かたまりの表示 span 列（表図形のセル本文など）を、Canvas 描画用の
 * セグメント列へレイアウトする。Markdown 表のセル（`wrapTableCell`）と同じ解決・折り返し・
 * グルーピングを通るため、装飾の見た目と折り返し位置が本文中の表と一致する。
 *
 * 座標は内容領域の左上を原点とし、`x` は左端からの相対位置、`baselineY` は各行のベースライン。
 * 文字色は解決しない（run を持たない区間は `Segment.color` を持たないため、呼び出し側の
 * 既定色で描かれる）。水平方向の打ち切りは呼び出し側のクリップに委ねる。
 */
export function layoutInlineSpansForCanvas(options: {
  readonly spans: readonly TextInlineDisplaySpan[];
  /** 行の基準フォントサイズ（出力 px）。 */
  readonly baseFontPx: number;
  /** 行全体の太字（表の列ヘッダーなど）。記法の太字はこれと独立に効く。 */
  readonly bold: boolean;
  /** 折り返し幅（px）。0 以下でも 1 文字は必ず出力する。 */
  readonly maxWidth: number;
  readonly measure: Measure;
  /**
   * 部分書式（run）の `fontSize`（保存 px）を出力 px へ換算する倍率。
   * 拡大・縮小して描く経路（PDF・サムネイル）では「出力 px ÷ 保存 px」を渡す。
   * 省略時は 1（保存 px と出力 px が同じ画面描画向け）。
   */
  readonly inlineFontScale?: number | undefined;
}): readonly Segment[] {
  const chars: StyledChar[] = [];
  for (const span of options.spans) {
    const resolved = resolveSpanStyle(span.style, span.decoration, {
      headingLine: false,
      inlineFontScale: options.inlineFontScale ?? 1,
      lineBold: options.bold,
      lineFontPx: options.baseFontPx,
    });
    appendStyledSpanChars(chars, span, resolved);
  }
  if (chars.length === 0) return [];
  const lines = wrapStyledChars({
    chars,
    firstLineWidth: options.maxWidth,
    measure: options.measure,
    wrappedWidth: options.maxWidth,
  });
  const segments: Segment[] = [];
  let lineTop = 0;
  for (const line of lines) {
    // ベースラインと行送りは、その行で最大のフォントサイズに合わせる（本文中の表と同じ規則）。
    const maxFontPx = line.reduce((max, item) => Math.max(max, item.fontPx), options.baseFontPx);
    pushWrappedLineSegments(segments, line, 0, lineTop + maxFontPx, options.measure);
    lineTop += maxFontPx * STICKY_LINE_HEIGHT_RATIO;
  }
  return segments;
}

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

/**
 * レイアウトの「描画領域」。callout の外は図形の全幅（`left: 0` / `width: maxWidth`）、
 * callout の中は左罫と内側余白を除いた領域になる。
 * 行のインデント・寄せ・折り返し幅の規則は変えず、基準にする幅と原点だけを差し替えるための型。
 */
interface ContentArea {
  /** 描画領域の左端（px）。 */
  readonly left: number;
  /** 描画領域の幅（px。0 未満にはしない）。 */
  readonly width: number;
}

/** 行 1 つのレイアウト結果（下端・その行の種別・幅超過の有無）。 */
interface LineLayoutResult {
  readonly bottom: number;
  readonly kind: MarkdownTextLine["kind"];
  readonly overflowsWidth: boolean;
}

/** 行 1 つを描画領域へ積む関数（通常経路と部分書式経路で実装が異なる）。 */
type LineLayoutFn = (params: {
  readonly options: Options;
  readonly sourceMap: MarkdownSourceMap;
  readonly lineIndex: number;
  readonly area: ContentArea;
  readonly top: number;
  readonly previousKind: MarkdownTextLine["kind"] | undefined;
  readonly segments: Segment[];
  readonly rects: LayoutRect[];
}) => LineLayoutResult;

/** callout のタイトル 1 段落を描画領域へ積む関数。 */
type TitleLayoutFn = (params: {
  readonly options: Options;
  readonly callout: MarkdownCallout;
  readonly area: ContentArea;
  readonly top: number;
  readonly segments: Segment[];
}) => { readonly bottom: number; readonly overflowsWidth: boolean };

/**
 * ブロック列をレイアウトする共通の駆動部。行とタイトルの積み方だけを引数で差し替え、
 * 表・コードブロック・callout の扱いと、ブロック間の空きの規則を 1 か所に保つ
 * （通常経路と部分書式経路で callout の外余白や検出順が食い違わないようにする）。
 */
function layoutMarkdownBlocks(
  options: Options,
  layoutLine: LineLayoutFn,
  layoutTitle: TitleLayoutFn
): Result {
  const { baseFontPx, maxWidth, text } = options;
  const segments: Segment[] = [];
  const rects: LayoutRect[] = [];
  const sourceMap = buildMarkdownSourceMap(text);
  // callout の外では描画領域が図形の全幅になり、既存の出力は 1px も変わらない。
  const area: ContentArea = { left: 0, width: maxWidth };
  let cursorY = 0;
  let overflowsWidth = false;
  // 直前に処理した行の種別。連続するリスト行の項目間隔（画面の marginTop）を再現するために持ち回る。
  let previousKind: MarkdownTextLine["kind"] | undefined;
  // 直前のブロックが callout か。DOM の margin 相殺と同じく、callout 同士の空きを合算しない。
  let previousWasCallout = false;
  // 保存済み列幅は本文中の表のブロック順で並ぶため、表だけを数える index で引く。
  let tableIndex = 0;
  for (const [blockIndex, block] of sourceMap.blocks.entries()) {
    if (block.kind === "table") {
      const laid = layoutTableBlock(
        blockLayoutContextOf(options),
        block.table,
        cursorY,
        segments,
        rects,
        tableColumnWidthsAt(options.tableColumnWidths, tableIndex)
      );
      tableIndex += 1;
      cursorY = laid.bottom;
      if (laid.overflowsWidth) overflowsWidth = true;
      // 表はリスト行ではないため、直後のリスト行に項目間隔を付けない（画面と同じ規則）。
      previousKind = undefined;
      previousWasCallout = false;
      continue;
    }
    if (block.kind === "code") {
      const laid = layoutCodeBlock(
        blockLayoutContextOf(options),
        sourceMap,
        block.code,
        cursorY,
        segments,
        rects
      );
      cursorY = laid.bottom;
      if (laid.overflowsWidth) overflowsWidth = true;
      previousKind = undefined;
      previousWasCallout = false;
      continue;
    }
    if (block.kind === "callout") {
      const marginPx = CALLOUT_MARGIN_Y_EM * baseFontPx;
      // 上の空き: 本文の先頭では 0（DOM の :first-child と同じ）。直前も callout なら、
      // その下の空きで既に 1 つぶん取ってあるため足さない（DOM の margin 相殺と同じ）。
      if (blockIndex > 0 && !previousWasCallout) cursorY += marginPx;
      const laid = layoutCalloutBlock({
        area,
        callout: block.callout,
        layoutLine,
        layoutTitle,
        options,
        rects,
        segments,
        sourceMap,
        top: cursorY,
      });
      cursorY = laid.bottom;
      if (laid.overflowsWidth) overflowsWidth = true;
      // 下の空き: 本文の末尾では 0（DOM の :last-child と同じ）。
      if (blockIndex < sourceMap.blocks.length - 1) cursorY += marginPx;
      previousKind = undefined;
      previousWasCallout = true;
      continue;
    }
    const laid = layoutLine({
      area,
      lineIndex: block.lineIndex,
      options,
      previousKind,
      rects,
      segments,
      sourceMap,
      top: cursorY,
    });
    cursorY = laid.bottom;
    if (laid.overflowsWidth) overflowsWidth = true;
    previousKind = laid.kind;
    previousWasCallout = false;
  }
  return { overflowsWidth, rects, segments, totalHeight: cursorY };
}

/** 通常経路（装飾も部分書式も無い本文）の行 1 つ。 */
const layoutPlainLine: LineLayoutFn = ({
  area,
  lineIndex,
  options,
  previousKind,
  rects,
  segments,
  sourceMap,
  top,
}) => {
  const { align, baseFontPx, measure } = options;
  const sourceLine = sourceMap.lines[lineIndex]!;
  // 番号付きリストの項番は他行に依存するため、source map の採番結果を渡す（旧 parseMarkdownTextLines と同じ値）。
  const line = markdownTextLineFromParts(sourceLine, {
    orderedLabel: sourceMap.orderedNumbers.get(sourceLine.lineIndex),
  });
  let cursorY = top;
  if (line.kind === "blank") {
    return {
      bottom: cursorY + baseFontPx * STICKY_LINE_HEIGHT_RATIO,
      kind: line.kind,
      overflowsWidth: false,
    };
  }
  if (line.kind === "rule") {
    // 水平線は文字を持たず、1 行分の高さの中央へ横罫を積む。
    return {
      bottom: pushRuleRect(rects, cursorY, baseFontPx, area),
      kind: line.kind,
      overflowsWidth: false,
    };
  }
  const bold = options.forceBold === true || line.kind === "heading";
  // 装飾を含まない本文だけがこの経路を通るため、斜体・等幅は常に false になる
  // （canvasFontFor の出力は従来と同一文字列になる）。
  const font: MeasureFont = { bold, italic: false, monospace: false };
  const fontPx = line.kind === "heading" ? baseFontPx * HEADING_FONT_SCALE[line.level] : baseFontPx;
  const lineHeight = fontPx * STICKY_LINE_HEIGHT_RATIO;
  // マーカー（`•` / `1.`）は本文と別のセグメントとして、マーカー領域の中央へ描く。
  // 本文と同じ文字列へ連結すると、1 行目の本文がマーカーの実描画幅ぶんしか右へ寄らず、
  // 折り返し行（マーカー領域の右端から始まる）と文頭がそろわない。
  const markerText = markdownListMarkerTextFor(line);
  const markerDrawnPx = markerText.length > 0 ? measure(markerText, fontPx, font) : 0;
  // 行の左右の余白（インデント）と寄せ。リスト行はぶら下げ、引用行は左罫ぶんの字下げ。
  const geometry = lineGeometryFor(line, fontPx, area.width, align, markerDrawnPx);
  // 連続するリスト行の項目間隔は、行の描画前に行送りへ加える（画面の marginTop と同じ位置）。
  cursorY += listItemGapPx(previousKind, line.kind, baseFontPx);
  const lineTop = cursorY;
  let overflowsWidth = markerOverflowsWidth(markerText, geometry, area);
  if (line.kind === "task") {
    pushTaskBoxRect(rects, line.checked, cursorY, lineHeight, fontPx, area, geometry);
    if (geometry.markerLeftPx + TASK_BOX_EM * fontPx > area.width) overflowsWidth = true;
  }
  if (markerText.length > 0) {
    segments.push({
      anchor: geometry.anchor,
      baselineY: cursorY + fontPx,
      bold,
      fontPx,
      text: markerText,
      x: area.left + markerStartXFor(geometry),
    });
  }
  const wrapped = wrapLineText({
    firstLineWidth: geometry.firstLineWidth,
    font,
    fontPx,
    measure,
    // 画面表示（MarkdownTextView）と同じく `[label](url)` は表示テキストへ縮約し、
    // インライン装飾のマーカーも取り除いたうえで折り返し・幅計算する
    // （PDF・エクスポート画像に raw 記法が出るのを防ぐ）。
    // リスト行はマーカーを別に描くため、折り返すのは本文だけにする。
    text: markdownLineBodyDisplayTextFor(line),
    wrappedWidth: geometry.wrappedWidth,
  });
  wrapped.forEach((segmentText, index) => {
    const indentPx = index === 0 ? geometry.firstLineIndentPx : geometry.wrappedIndentPx;
    if (measure(segmentText, fontPx, font) > Math.max(0, area.width - indentPx)) {
      overflowsWidth = true;
    }
    // 本文が空のリスト行（`- ` だけ）では本文セグメントを積まない（マーカーは上で積んである）。
    if (markerText.length === 0 || segmentText.length > 0) {
      segments.push({
        anchor: geometry.anchor,
        baselineY: cursorY + fontPx,
        bold,
        fontPx,
        text: segmentText,
        x: area.left + anchorXFor(geometry.anchor, indentPx, area.width),
      });
    }
    cursorY += lineHeight;
  });
  if (line.kind === "quote") {
    // 左罫は、その論理行が折り返して占めた高さの合計ぶん（1 行分に固定しない）。
    pushQuoteBarRect(rects, lineTop, cursorY - lineTop, fontPx, area);
  }
  return { bottom: cursorY, kind: line.kind, overflowsWidth };
};

/**
 * 部分書式つき本文の行 1 つ。
 *
 * 行の表示テキストを「リンク境界 × run 境界」で分割（domain の共有関数）したうえで、
 * 文字単位の詰め込み方式で折り返す。1 文字ごとに有効なフォント（サイズ・太字）で幅を測るため、
 * 部分サイズが混ざる行でも折り返し位置が実際の描画と一致する。
 *
 * 中央・右寄せでは、折り返し後の行の実幅から開始 X を求めて左基準で並べる
 * （複数の書式区間を 1 行へ並べるため、ctx.textAlign のアンカー配置は使えない）。
 */
const layoutStyledLine: LineLayoutFn = ({
  area,
  lineIndex,
  options,
  previousKind,
  rects,
  segments,
  sourceMap,
  top,
}) => {
  const { align, baseFontPx, measure, text } = options;
  const inlineFontScale = options.inlineFontScale ?? 1;
  const sourceLine = sourceMap.lines[lineIndex]!;
  // 番号付きリストの項番は他行に依存するため、source map の採番結果を渡す（通常経路と同じ値になる）。
  const line = markdownTextLineFromParts(sourceLine, {
    orderedLabel: sourceMap.orderedNumbers.get(sourceLine.lineIndex),
  });
  let cursorY = top;
  if (line.kind === "blank") {
    return {
      bottom: cursorY + baseFontPx * STICKY_LINE_HEIGHT_RATIO,
      kind: line.kind,
      overflowsWidth: false,
    };
  }
  if (line.kind === "rule") {
    // 水平線は文字を持たず、1 行分の高さの中央へ横罫を積む（通常経路と同じ）。
    return {
      bottom: pushRuleRect(rects, cursorY, baseFontPx, area),
      kind: line.kind,
      overflowsWidth: false,
    };
  }
  const lineBold = options.forceBold === true || line.kind === "heading";
  const lineFontPx =
    line.kind === "heading" ? baseFontPx * HEADING_FONT_SCALE[line.level] : baseFontPx;
  // 箇条書きのグリフ（`•`）と番号付きリストの項番（`1.`）は run を持たない表示専用の接頭辞で、
  // 本文とは別のセグメントとして描く（通常経路と同じ規則）。
  const markerText = markdownListMarkerTextFor(line);
  const markerFont: MeasureFont = { bold: lineBold, italic: false, monospace: false };
  const markerDrawnPx = markerText.length > 0 ? measure(markerText, lineFontPx, markerFont) : 0;
  // 行の左右の余白（インデント）と寄せは通常経路と同じ規則で求める。
  const geometry = lineGeometryFor(line, lineFontPx, area.width, align, markerDrawnPx);
  const { firstLineIndentPx, wrappedIndentPx } = geometry;
  const lineAnchor = geometry.anchor;
  // 連続するリスト行の項目間隔は、行の描画前に行送りへ加える（部分書式の有無で見た目を変えない）。
  cursorY += listItemGapPx(previousKind, line.kind, baseFontPx);
  const lineTop = cursorY;
  if (line.kind === "task") {
    pushTaskBoxRect(
      rects,
      line.checked,
      cursorY,
      lineFontPx * STICKY_LINE_HEIGHT_RATIO,
      lineFontPx,
      area,
      geometry
    );
  }
  const chars: StyledChar[] = [];
  for (const span of buildTextInlineDisplaySpans({
    line: sourceLine,
    runs: options.inlineStyles,
    text,
  })) {
    const resolved = resolveSpanStyle(span.style, span.decoration, {
      headingLine: line.kind === "heading",
      inlineFontScale,
      lineBold,
      lineFontPx,
    });
    appendStyledSpanChars(chars, span, resolved);
  }
  let overflowsWidth = markerOverflowsWidth(markerText, geometry, area);
  if (line.kind === "task" && geometry.markerLeftPx + TASK_BOX_EM * lineFontPx > area.width) {
    overflowsWidth = true;
  }
  const wrapped = wrapStyledChars({
    chars,
    firstLineWidth: geometry.firstLineWidth,
    measure,
    wrappedWidth: geometry.wrappedWidth,
  });
  wrapped.forEach((wrappedLine, index) => {
    const indentPx = index === 0 ? firstLineIndentPx : wrappedIndentPx;
    const available = Math.max(0, area.width - indentPx);
    // 行幅は、部分書式・装飾ごとに異なるフォントで測った 1 文字ぶんの幅（+ コードの padding）の合計。
    const lineWidth = styledLineWidth(wrappedLine, measure);
    if (lineWidth > available) overflowsWidth = true;
    // 行送りは行内で最大のフォントサイズに合わせる（部分サイズが行高を決める）。
    const maxFontPx = wrappedLine.reduce((max, item) => Math.max(max, item.fontPx), lineFontPx);
    // マーカーは本文の 1 行目とベースラインを共有する（本文の部分サイズが行高を決めるため、
    // 行送りが確定する 1 行目の中で積む）。
    if (index === 0 && markerText.length > 0) {
      segments.push({
        anchor: lineAnchor,
        baselineY: cursorY + maxFontPx,
        bold: lineBold,
        fontPx: lineFontPx,
        text: markerText,
        x: area.left + markerStartXFor(geometry),
      });
    }
    // 中央・右寄せは、インデント（引用の左罫ぶんを含む）より右側の利用可能幅の中で寄せる。
    const startX =
      lineAnchor === "center"
        ? indentPx + Math.max(0, (available - lineWidth) / 2)
        : lineAnchor === "right"
          ? indentPx + Math.max(0, available - lineWidth)
          : indentPx;
    pushWrappedLineSegments(
      segments,
      wrappedLine,
      area.left + startX,
      cursorY + maxFontPx,
      measure
    );
    cursorY += maxFontPx * STICKY_LINE_HEIGHT_RATIO;
  });
  if (line.kind === "quote") {
    // 左罫は、その論理行が折り返して占めた高さの合計ぶん（通常経路と同じ）。
    pushQuoteBarRect(rects, lineTop, cursorY - lineTop, lineFontPx, area);
  }
  return { bottom: cursorY, kind: line.kind, overflowsWidth };
};

//------------------------------------------------------------------------------
// callout（Connect Note 互換の `:::note`）
//------------------------------------------------------------------------------

/**
 * callout 1 つをレイアウトし、背景と左罫を `rects` へ、タイトルと本文行を `segments` へ積む。
 * 戻り値はブロックの下端（外余白を含まない）と幅超過の有無。
 *
 * - フェンス行はセグメントも高さも出さない（DOM でも表示しない）。
 * - 本文行は callout の外とまったく同じ関数（`layoutLine`）を、内側の描画領域で呼ぶ。
 *   寄せ・インデント・折り返し幅の規則は変えず、基準幅と原点だけが内側領域になる。
 * - 左罫の固定 px 下限は出力スケール（`pxScale`）で換算する。レイアウト座標は出力解像度の
 *   座標系のため、CSS の 4px をそのまま使うと倍率が 1 以外の出力で罫の太さと本文の開始位置が
 *   画面と食い違う。換算に使うのは、表の外枠やコードブロックの枠線と同じ `pxScale` であって
 *   `inlineFontScale`（部分書式の保存 px を換算する倍率）ではない。付箋の PDF 出力は
 *   `inlineFontScale` を渡さない（既定 1）ため、取り違えると縮小・拡大出力で罫だけが画面と食い違う。
 * - 描画領域の幅は 0 未満にしない。狭い図形や大きなフォントでは左右の余白と左罫の合計が
 *   `maxWidth` を超え得るが、負の幅を渡すと PDF は逆方向へ塗り、サムネイルは描画を飛ばして
 *   経路間で見た目が食い違う。
 * - `rects` は「背景 → 左罫 → 中身の行が生む罫」の順で積む。PDF もサムネイルも配列順に塗るため、
 *   背景を後から積むと内側の罫を上塗りしてしまう。高さはブロックの終わりに差し替える
 *   （高さを後から入れるだけで、配列の順序は変わらない）。
 */
function layoutCalloutBlock(params: {
  readonly options: Options;
  readonly sourceMap: MarkdownSourceMap;
  readonly callout: MarkdownCallout;
  readonly area: ContentArea;
  readonly top: number;
  readonly segments: Segment[];
  readonly rects: LayoutRect[];
  readonly layoutLine: LineLayoutFn;
  readonly layoutTitle: TitleLayoutFn;
}): { readonly bottom: number; readonly overflowsWidth: boolean } {
  const { area, callout, options, rects, segments, sourceMap, top } = params;
  const { baseFontPx, text } = options;
  const colors = CALLOUT_COLORS[options.calloutOnDark === true ? "onDark" : "light"][callout.type];
  const accentPx = Math.max(
    CALLOUT_ACCENT_MIN_PX * (options.pxScale ?? 1),
    CALLOUT_ACCENT_EM * baseFontPx
  );
  const padXPx = CALLOUT_PAD_X_EM * baseFontPx;
  const padYPx = CALLOUT_PAD_Y_EM * baseFontPx;
  const inner: ContentArea = {
    left: area.left + accentPx + padXPx,
    width: Math.max(0, area.width - accentPx - padXPx * 2),
  };
  const backgroundIndex = rects.length;
  rects.push({ fill: colors.bg, h: 0, role: "callout-bg", w: area.width, x: area.left, y: top });
  const accentIndex = rects.length;
  rects.push({
    fill: colors.accent,
    h: 0,
    role: "callout-accent",
    w: accentPx,
    x: area.left,
    y: top,
  });
  let cursorY = top + padYPx;
  let overflowsWidth = false;
  if (markdownCalloutHasTitle(text, callout)) {
    const laid = params.layoutTitle({ area: inner, callout, options, segments, top: cursorY });
    cursorY = laid.bottom;
    if (laid.overflowsWidth) overflowsWidth = true;
    // タイトルと本文行の両方があるときだけ間隔を空ける（DOM ではタイトルだけの callout で
    // `.canvas-md-callout > :last-child` が margin-bottom を打ち消す）。
    if (callout.body.length > 0) cursorY += CALLOUT_TITLE_GAP_EM * baseFontPx;
  } else if (callout.body.length === 0) {
    // 本文 0 行・タイトル無しは、DOM 側の `<br>` だけの空行と同じ 1 行分の高さを確保する。
    cursorY += baseFontPx * STICKY_LINE_HEIGHT_RATIO;
  }
  let previousKind: MarkdownTextLine["kind"] | undefined;
  for (const body of callout.body) {
    const laid = params.layoutLine({
      area: inner,
      lineIndex: body.lineIndex,
      options,
      previousKind,
      rects,
      segments,
      sourceMap,
      top: cursorY,
    });
    cursorY = laid.bottom;
    previousKind = laid.kind;
    if (laid.overflowsWidth) overflowsWidth = true;
  }
  const blockHeight = cursorY + padYPx - top;
  rects[backgroundIndex] = { ...rects[backgroundIndex]!, h: blockHeight };
  rects[accentIndex] = { ...rects[accentIndex]!, h: blockHeight };
  return { bottom: top + blockHeight, overflowsWidth };
}

/** 通常経路の callout タイトル（基底の太字だけを持つ 1 段落）。 */
const layoutPlainCalloutTitle: TitleLayoutFn = ({ area, callout, options, segments, top }) => {
  const { align, baseFontPx, measure, text } = options;
  const font: MeasureFont = { bold: true, italic: false, monospace: false };
  const lineHeight = baseFontPx * STICKY_LINE_HEIGHT_RATIO;
  let cursorY = top;
  let overflowsWidth = false;
  for (const segmentText of wrapLineText({
    firstLineWidth: area.width,
    font,
    fontPx: baseFontPx,
    measure,
    text: markdownCalloutTitleDisplayText(text, callout),
    wrappedWidth: area.width,
  })) {
    if (measure(segmentText, baseFontPx, font) > Math.max(0, area.width)) overflowsWidth = true;
    segments.push({
      anchor: align,
      baselineY: cursorY + baseFontPx,
      bold: true,
      fontPx: baseFontPx,
      text: segmentText,
      x: area.left + anchorXFor(align, 0, area.width),
    });
    cursorY += lineHeight;
  }
  return { bottom: cursorY, overflowsWidth };
};

/**
 * 部分書式つき本文の callout タイトル。
 * 「行の基底の太字」として `lineBold: true` / `headingLine: false` で解決するため、
 * run の `bold: false` はタイトルの太字を打ち消す（DOM の `font-weight: 700` を
 * 子 span の `font-weight: 400` が打ち消すのと同じ挙動）。
 */
const layoutStyledCalloutTitle: TitleLayoutFn = ({ area, callout, options, segments, top }) => {
  const { align, baseFontPx, measure, text } = options;
  const chars: StyledChar[] = [];
  for (const span of markdownCalloutTitleSpans(text, callout, options.inlineStyles)) {
    const resolved = resolveSpanStyle(span.style, span.decoration, {
      headingLine: false,
      inlineFontScale: options.inlineFontScale ?? 1,
      lineBold: true,
      lineFontPx: baseFontPx,
    });
    appendStyledSpanChars(chars, span, resolved);
  }
  let cursorY = top;
  let overflowsWidth = false;
  for (const wrappedLine of wrapStyledChars({
    chars,
    firstLineWidth: area.width,
    measure,
    wrappedWidth: area.width,
  })) {
    const lineWidth = styledLineWidth(wrappedLine, measure);
    if (lineWidth > Math.max(0, area.width)) overflowsWidth = true;
    // 行送りは行内で最大のフォントサイズに合わせる（部分サイズが行高を決める）。
    const maxFontPx = wrappedLine.reduce((max, item) => Math.max(max, item.fontPx), baseFontPx);
    const startX =
      align === "center"
        ? Math.max(0, (area.width - lineWidth) / 2)
        : align === "right"
          ? Math.max(0, area.width - lineWidth)
          : 0;
    pushWrappedLineSegments(
      segments,
      wrappedLine,
      area.left + startX,
      cursorY + maxFontPx,
      measure
    );
    cursorY += maxFontPx * STICKY_LINE_HEIGHT_RATIO;
  }
  return { bottom: cursorY, overflowsWidth };
};

/**
 * 連続するリスト行の項目間隔（px）。直前の行と今の行の両方がリスト行（isMarkdownListLineKind）の
 * ときだけ、画面の marginTop（BULLET_ITEM_GAP_EM）と同じ比率で基準フォントに対する px を返す。
 * 判定を述語へ委ねることで、番号付きリスト等を述語へ足したときに Canvas 側だけ間隔が
 * 付かなくなることを防ぐ。リスト行は基準フォントで描くため、em の基準も baseFontPx になる。
 */
function listItemGapPx(
  previousKind: MarkdownTextLine["kind"] | undefined,
  currentKind: MarkdownTextLine["kind"],
  baseFontPx: number
): number {
  if (previousKind === undefined) return 0;
  if (!isMarkdownListLineKind(previousKind) || !isMarkdownListLineKind(currentKind)) return 0;
  return BULLET_ITEM_GAP_EM * baseFontPx;
}

/** 1 論理行の左右の余白（インデント）と寄せ。通常経路と部分書式経路が同じ規則を共有する。 */
interface LineGeometry {
  /**
   * **本文**の 1 行目（折り返し前）の左インデント（px）。
   * リスト行ではマーカー領域の右端であり、マーカー自身を描く位置ではない（それは markerLeftPx）。
   */
  readonly firstLineIndentPx: number;
  /**
   * **本文**の 2 行目以降（折り返し行）の左インデント（px）。
   * リスト行では**見積りのマーカー領域の右端で固定**する。DOM の折り返し行は行の `padding-left`
   * （`階層 × 2em + --marker-width` の固定値）から始まり、マーカー span の実描画幅には追従しない。
   * ここを実測幅へ合わせると、画面（DOM）と出力（PDF・サムネイル）で折り返し行の位置が食い違う。
   */
  readonly wrappedIndentPx: number;
  /** 1 行目の折り返し幅（px）。 */
  readonly firstLineWidth: number;
  /** 2 行目以降の折り返し幅（px）。 */
  readonly wrappedWidth: number;
  /** この行の寄せ（リスト行は常に左）。 */
  readonly anchor: Anchor;
  /** マーカー領域の左端（px）。リスト行以外は 0。DOM の「行の左端 + 階層インデント」に対応する。 */
  readonly markerLeftPx: number;
  /** マーカー領域の幅（px。見積り）。リスト行以外は 0。DOM の `--marker-width` に対応する。 */
  readonly markerWidthPx: number;
  /** マーカー文字列の実測幅（px）。リスト行以外は 0。 */
  readonly markerDrawnPx: number;
}

/**
 * 行種別ごとのインデントと寄せを求める。画面表示の paddingLeft / textIndent（em 指定）と同じ比率。
 * - 箇条書き・番号付きリスト: マーカー領域（markdownListMarkerWidthEm）を階層インデントの右へ
 *   確保し、**本文は 1 行目も折り返し行も同じ位置**から始める（ハンギングインデント）。寄せは常に左。
 *   マーカーの実測幅が見積りを超えた行だけ 1 行目の本文が右へ動く。これは DOM 側でマーカー span の
 *   `min-width` が伸びたときの挙動と一致する（DOM の 1 行目は伸びた span の右から本文が始まり、
 *   折り返し行は固定値の `padding-left` から始まるため、同じ量だけ 1 行目がずれる）。
 *   **折り返し行を実測幅へそろえて「Canvas だけ」ずれを消してはいけない。** 画面と出力の位置が
 *   食い違い、PDF・サムネイルが画面の再現でなくなる。この残差を無くすには DOM の版組み方式
 *   （`padding-left` + 負の `text-indent`）自体を変える必要があるため、範囲外としている。
 *   見積りは項番に使う文字（半角の数字・ハイフン・ピリオド）に対して
 *   安全側に取ってあり、通常のフォント・項番では発動しない（E2E の E-5 で実測して固定している）。
 * - 引用: DOM の border-left + padding-left（MARKDOWN_QUOTE_INDENT_EM）ぶん右から本文が始まり、
 *   折り返し幅もそのぶん狭い。寄せは align に従う（中央・右寄せは左罫より右側の領域の中で寄せる）。
 * - 見出し・plain: インデント 0。寄せは align に従う。
 *
 * `markerDrawnPx` は呼び出し側がマーカー文字列を実測して渡す（この関数は計測関数を持たない）。
 */
function lineGeometryFor(
  line: MarkdownTextLine,
  fontPx: number,
  maxWidth: number,
  align: Anchor,
  markerDrawnPx: number
): LineGeometry {
  if (line.kind === "bullet" || line.kind === "ordered" || line.kind === "task") {
    const markerLeftPx = line.level * BULLET_INDENT_EM * fontPx;
    const markerWidthPx = markdownListMarkerWidthEm(line) * fontPx;
    // 折り返し行は見積り幅で固定する（DOM の padding-left は `--marker-width` の固定値のため）。
    // 1 行目だけが実測幅へ追従するのも DOM と同じで、意図的に一致させている（上のコメント参照）。
    const wrappedIndentPx = markerLeftPx + markerWidthPx;
    const firstLineIndentPx = markerLeftPx + Math.max(markerWidthPx, markerDrawnPx);
    return {
      anchor: "left",
      firstLineIndentPx,
      // マーカー領域だけで図形の幅を使い切る場合でも、折り返し幅は 0 未満にしない。
      firstLineWidth: Math.max(0, maxWidth - firstLineIndentPx),
      markerDrawnPx,
      markerLeftPx,
      markerWidthPx,
      wrappedIndentPx,
      wrappedWidth: Math.max(0, maxWidth - wrappedIndentPx),
    };
  }
  if (line.kind === "quote") {
    const indentPx = MARKDOWN_QUOTE_INDENT_EM * fontPx;
    return {
      anchor: align,
      firstLineIndentPx: indentPx,
      firstLineWidth: maxWidth - indentPx,
      markerDrawnPx: 0,
      markerLeftPx: 0,
      markerWidthPx: 0,
      wrappedIndentPx: indentPx,
      wrappedWidth: maxWidth - indentPx,
    };
  }
  return {
    anchor: align,
    firstLineIndentPx: 0,
    firstLineWidth: maxWidth,
    markerDrawnPx: 0,
    markerLeftPx: 0,
    markerWidthPx: 0,
    wrappedIndentPx: 0,
    wrappedWidth: maxWidth,
  };
}

/**
 * マーカーの描画開始 X（描画領域の左端からの相対）。マーカー領域の中で中央へ置く
 * （DOM の `.canvas-md-list-marker { text-align: center }` と一致させる）。
 * 実測幅が領域より広い行では領域の左端から描く（`min-width` が伸びたときの DOM と同じ）。
 */
function markerStartXFor(geometry: LineGeometry): number {
  return geometry.markerLeftPx + Math.max(0, (geometry.markerWidthPx - geometry.markerDrawnPx) / 2);
}

/** task 行のチェックボックス矩形を、階層インデントの直後へ積む。 */
function pushTaskBoxRect(
  rects: LayoutRect[],
  checked: boolean,
  top: number,
  lineHeight: number,
  fontPx: number,
  area: ContentArea,
  geometry: LineGeometry
): void {
  const size = TASK_BOX_EM * fontPx;
  rects.push({
    h: size,
    role: checked ? "task-box-checked" : "task-box",
    w: size,
    x: area.left + geometry.markerLeftPx,
    y: top + Math.max(0, (lineHeight - size) / 2),
  });
}

/**
 * マーカー自身が描画領域からはみ出すか。マーカーを本文と別セグメントにしたことで、本文が空の
 * リスト行（`- ` だけ）では本文側の幅超過判定が働かなくなる。付箋のフォント自動調整は
 * `overflowsWidth` で「収まらない」を判定するため、マーカーの分をここで補う。
 */
function markerOverflowsWidth(
  markerText: string,
  geometry: LineGeometry,
  area: ContentArea
): boolean {
  if (markerText.length === 0) return false;
  return geometry.markerDrawnPx > Math.max(0, area.width - geometry.markerLeftPx);
}

/**
 * 通常経路（ctx.textAlign のアンカー配置）でのアンカー X。
 * 左寄せはインデント位置、中央寄せはインデントより右側の領域の中央、右寄せは右端。
 * インデントが 0 の行（見出し・plain）では従来の maxWidth / 2・maxWidth と同じ値になる。
 */
function anchorXFor(anchor: Anchor, indentPx: number, maxWidth: number): number {
  if (anchor === "center") return indentPx + (maxWidth - indentPx) / 2;
  if (anchor === "right") return maxWidth;
  return indentPx;
}

/**
 * 水平線の横罫を積み、1 行分の高さを進めた cursorY を返す。
 * 太さは DOM の `max(1px, 0.0625em)` と同じ式で、上端は行の中央に線の中心が来る位置。
 * 横罫は描画領域いっぱいに引く（callout の中では内側領域まで。枠を突き抜けさせない）。
 * 描画領域の幅が 0 の場合は罫を積まない（幅 0 の矩形を経路ごとに違う扱いにしないため）。
 */
function pushRuleRect(
  rects: LayoutRect[],
  cursorY: number,
  baseFontPx: number,
  area: ContentArea
): number {
  const lineHeight = baseFontPx * STICKY_LINE_HEIGHT_RATIO;
  const h = Math.max(1, baseFontPx * MARKDOWN_RULE_THICKNESS_EM);
  if (area.width > 0) {
    rects.push({
      h,
      role: "rule",
      w: area.width,
      x: area.left,
      y: cursorY + (lineHeight - h) / 2,
    });
  }
  return cursorY + lineHeight;
}

/**
 * 引用の左罫を積む。左端は描画領域の左端（callout の中では内側領域の左端。callout の左罫と
 * 重ならない）、幅は DOM の border-left（MARKDOWN_QUOTE_BAR_EM）と同じ比率、
 * 高さはその論理行が折り返して占めた高さの合計（連続する引用行では罫が隙間なくつながる）。
 */
function pushQuoteBarRect(
  rects: LayoutRect[],
  top: number,
  height: number,
  fontPx: number,
  area: ContentArea
): void {
  rects.push({
    h: height,
    role: "quote-bar",
    w: MARKDOWN_QUOTE_BAR_EM * fontPx,
    x: area.left,
    y: top,
  });
}

/**
 * 表示 span 1 つぶんの文字を、解決済みの描画属性で `chars` へ積む。
 * インラインコードの左右 padding は、論理コード範囲の先頭・末尾へ 1 回ずつだけ加える
 * （DOM の padding-inline と同じ回数。run や折り返しで span が分かれても増えない）。
 * 部分書式経路の行と、表のセルが共有する。
 */
function appendStyledSpanChars(
  chars: StyledChar[],
  span: TextInlineDisplaySpan,
  resolved: Omit<StyledChar, "character">
): void {
  const characters = Array.from(span.text);
  for (const [index, character] of characters.entries()) {
    const padBefore =
      span.codeStart === true && index === 0 ? INLINE_CODE_PADDING_EM * resolved.fontPx : 0;
    const padAfter =
      span.codeEnd === true && index === characters.length - 1
        ? INLINE_CODE_PADDING_EM * resolved.fontPx
        : 0;
    chars.push({
      ...resolved,
      character,
      ...(span.codeRangeIndex !== undefined ? { codeRangeIndex: span.codeRangeIndex } : {}),
      ...(padBefore > 0 ? { padBefore } : {}),
      ...(padAfter > 0 ? { padAfter } : {}),
    });
  }
}

/** 折り返し済みの 1 行の幅（各文字を自分のフォントで測った送り幅の合計）。 */
function styledLineWidth(wrappedLine: readonly StyledChar[], measure: Measure): number {
  // 1 文字ぶんの送り幅（実測幅 + インラインコードの padding）を足し合わせる。
  return wrappedLine.reduce((sum, item) => sum + styledCharAdvance(item, measure), 0);
}

/**
 * 折り返し済みの 1 行を、同じ描画属性が続く区間ごとのセグメントとして `segments` へ積む。
 * `startX` は行の左端（インデント・寄せを解決済み）、`baselineY` はベースライン位置。
 * 部分書式経路の行と、表のセルが共有する。
 */
function pushWrappedLineSegments(
  segments: Segment[],
  wrappedLine: readonly StyledChar[],
  startX: number,
  baselineY: number,
  measure: Measure
): void {
  let offsetX = 0;
  for (const piece of groupStyledChars(wrappedLine)) {
    const width = measure(piece.text, piece.fontPx, measureFontOf(piece));
    const padLeft = piece.padBefore ?? 0;
    const padRight = piece.padAfter ?? 0;
    segments.push({
      anchor: "left",
      baselineY,
      bold: piece.bold,
      fontPx: piece.fontPx,
      text: piece.text,
      width,
      // x は glyph の描画位置。背景矩形は padLeftPx / padRightPx のぶん外側へ広がる。
      x: startX + offsetX + padLeft,
      ...(piece.italic === true ? { italic: true } : {}),
      ...(piece.strikethrough === true ? { strikethrough: true } : {}),
      ...(piece.monospace === true ? { monospace: true } : {}),
      ...(piece.codeRangeIndex !== undefined ? { codeRangeIndex: piece.codeRangeIndex } : {}),
      ...(padLeft > 0 ? { padLeftPx: padLeft } : {}),
      ...(padRight > 0 ? { padRightPx: padRight } : {}),
      ...(piece.color !== undefined ? { color: piece.color } : {}),
      ...(piece.background !== undefined ? { background: piece.background } : {}),
    });
    offsetX += padLeft + width + padRight;
  }
}

//------------------------------------------------------------------------------
// 表（GFM 基本形）
//------------------------------------------------------------------------------

/**
 * 複数行ブロック（表・コードブロック）のレイアウトに必要な値。
 * 通常経路・部分書式経路の両方が Options から同じ値を取り出す。
 */
interface BlockLayoutContext {
  readonly text: string;
  readonly baseFontPx: number;
  readonly maxWidth: number;
  readonly measure: Measure;
  readonly pxScale: number;
  readonly inlineFontScale: number;
  readonly inlineStyles: readonly TextInlineStyleRange[] | undefined;
  readonly forceBold: boolean;
  readonly tableOnDark: boolean;
  readonly tableBodyPalette: MarkdownTableBodyPalette;
  readonly bodyColor: string;
}

function blockLayoutContextOf(options: Options): BlockLayoutContext {
  const tableOnDark = options.tableBodyPalette?.onDark ?? options.tableOnDark === true;
  return {
    baseFontPx: options.baseFontPx,
    forceBold: options.forceBold === true,
    inlineFontScale: options.inlineFontScale ?? 1,
    inlineStyles: options.inlineStyles,
    maxWidth: options.maxWidth,
    measure: options.measure,
    pxScale: options.pxScale ?? 1,
    tableOnDark,
    tableBodyPalette: options.tableBodyPalette ?? {
      bodyBackground: MARKDOWN_TABLE_BODY_BACKGROUND_COLOR,
      stripeBackground: tableOnDark
        ? MARKDOWN_TABLE_ROW_STRIPE_COLOR_ON_DARK
        : MARKDOWN_TABLE_ROW_STRIPE_COLOR,
      onDark: tableOnDark,
    },
    bodyColor: options.bodyColor ?? "#1a1a2e",
    text: options.text,
  };
}

/** セル 1 つの折り返し結果と、その占有高さ（行送りの合計）。 */
interface WrappedTableCell {
  readonly lines: readonly (readonly StyledChar[])[];
  /** 各行の行送り（行内で最大のフォントサイズ × 行高比）。 */
  readonly lineHeights: readonly number[];
  readonly height: number;
}

/**
 * 表 1 つをレイアウトし、外枠・列ヘッダー背景・列ヘッダーの区切り線を `rects` へ、セルの本文を
 * `segments` へ積む。戻り値は表の下端（次のブロックの cursorY）と幅超過の有無。
 *
 * - 列幅（em）は画面と同じ共有関数で決め、`availableEm = (maxWidth − 外枠線 2 本) ÷ baseFontPx`
 *   も同じ式（markdownTableAvailableEm）で求める。DOM は外枠線 2px を差し引いているため、
 *   Canvas も差し引かないと共有関数への入力がずれ、列境界と切り取り位置が食い違う。
 * - 外枠線は画面の CSS 1px を出力スケール（pxScale）で換算する。
 * - セルは左右 padding（TABLE_CELL_PADDING_EM ÷ 2 ずつ）の内側で、部分書式経路と同じ
 *   1 文字単位の詰め込みで折り返す。行の高さは「セル内で最も行数の多いセルの高さ」と
 *   最低高さ（TABLE_ROW_MIN_HEIGHT_EM）の大きい方。本文は各セルの上端から描く（vertical-align: top）。
 * - 列ヘッダー（ヘッダ行）は太字・白文字で、背景と列境界の白い縦線を rects で積む。
 * - 最小列幅の合計すら使用可能幅を超える表は、DOM の wrapper（`overflow: hidden`）と同じく
 *   maxWidth で打ち切って描き、overflowsWidth を真にする（付箋のフォント自動調整が縮小する）。
 * - rects は外枠 → 本文の縞 → ヘッダ背景 → 区切り線 → 下枠の描画順で積む。
 *   サムネイルは面積の大きい縞を読み飛ばし、それ以外の構造だけを抽象表示する。
 */
function layoutTableBlock(
  context: BlockLayoutContext,
  table: MarkdownTable,
  top: number,
  segments: Segment[],
  rects: LayoutRect[],
  savedWidths: TableColumnWidths | null
): { readonly bottom: number; readonly overflowsWidth: boolean } {
  const { baseFontPx, maxWidth, measure, pxScale, text } = context;
  const borderPx = TABLE_BORDER_PX * pxScale;
  const availableEm = markdownTableAvailableEm(maxWidth, baseFontPx, pxScale);
  const columnWidthsPx = markdownTableColumnWidthsEm(text, table, availableEm, savedWidths).map(
    (em) => em * baseFontPx
  );
  // 列幅（外枠を含まない内側の幅）の合計。表の外寸は、これに左右の外枠線を足した値になる。
  const columnsWidthPx = columnWidthsPx.reduce((sum, width) => sum + width, 0);
  const tableWidthPx = columnsWidthPx + borderPx * 2;
  // 列幅の合計が使用可能幅に収まるあいだは表の幅が maxWidth を超えない（共有関数の保証）。
  // 超えるのは最小列幅の合計が使用可能幅を超える場合だけで、DOM と同じく maxWidth で打ち切る。
  const overflowsWidth = tableWidthPx > maxWidth + 1e-6;
  const visibleWidthPx = Math.min(maxWidth, tableWidthPx);
  // wrapper の内側（外枠線の内側）で切り取られる右端。ヘッダ背景・区切り線・本文はここより右へ出さない。
  const innerClipRight = visibleWidthPx - borderPx;
  const cellPadPx = (TABLE_CELL_PADDING_EM / 2) * baseFontPx;
  const rowMinHeightPx = TABLE_ROW_MIN_HEIGHT_EM * baseFontPx;
  // 列の左端（= 左の外枠線 + それより前の列の幅の合計）を先に 1 回だけ積算する。
  // セルごとに前列を数え直すと、列数の 2 乗に比例して重くなる。
  const columnLeftsPx: number[] = [];
  let columnLeft = borderPx;
  for (const width of columnWidthsPx) {
    columnLeftsPx.push(columnLeft);
    columnLeft += width;
  }
  const columnLeftPx = (column: number): number => columnLeftsPx[column] ?? columnLeft;

  // 1 周目: 各セルを折り返し、行の高さを確定する（左右の外枠の高さは表全体の高さが要るため）。
  const rows = [
    { bodyIndex: undefined, header: true, row: table.header },
    ...table.body.map((row, bodyIndex) => ({ bodyIndex, header: false, row })),
  ];
  const wrappedRows = rows.map(({ bodyIndex, header, row }) => {
    const rowBackground =
      bodyIndex === undefined || (bodyIndex + 1) % 2 === 1
        ? context.tableBodyPalette.bodyBackground
        : context.tableBodyPalette.stripeBackground;
    const cells = row.cells.map((cell, column) =>
      wrapTableCell(
        context,
        cell,
        header,
        Math.max(0, (columnWidthsPx[column] ?? 0) - cellPadPx * 2),
        rowBackground
      )
    );
    const height = Math.max(rowMinHeightPx, ...cells.map((cell) => cell.height));
    return { bodyIndex, cells, header, height };
  });
  // 表の外寸の高さ = 上下の外枠線 + 各行の高さの合計。
  const totalHeight = borderPx * 2 + wrappedRows.reduce((sum, row) => sum + row.height, 0);

  // 外枠（上・左・右）。右枠は wrapper の右端（打ち切り時は maxWidth）に付く。
  rects.push({ h: borderPx, role: "table-border", w: visibleWidthPx, x: 0, y: top });
  rects.push({ h: totalHeight, role: "table-border", w: borderPx, x: 0, y: top });
  rects.push({
    h: totalHeight,
    role: "table-border",
    w: borderPx,
    x: visibleWidthPx - borderPx,
    y: top,
  });

  // 本文の全行へ不透明な面を積み、奇数行は本文面、偶数行は既存の縞として描き分ける。
  // ヘッダ背景・列境界より先に積み、描画順が将来重なっても縞が上塗りしないようにする。
  let stripeTop = top + borderPx;
  const stripeWidth = Math.min(columnsWidthPx, innerClipRight - borderPx);
  for (const wrappedRow of wrappedRows) {
    if (wrappedRow.bodyIndex !== undefined && stripeWidth > 0) {
      const even = (wrappedRow.bodyIndex + 1) % 2 === 0;
      rects.push({
        h: wrappedRow.height,
        role: even
          ? context.tableOnDark
            ? "table-row-stripe-on-dark"
            : "table-row-stripe"
          : "table-body-bg",
        w: stripeWidth,
        x: borderPx,
        y: stripeTop,
      });
    }
    stripeTop += wrappedRow.height;
  }

  // 2 周目: 行ごとにヘッダ背景・区切り線と本文を積む。
  let rowTop = top + borderPx;
  for (const wrappedRow of wrappedRows) {
    if (wrappedRow.header) {
      const bgWidth = Math.min(columnsWidthPx, innerClipRight - borderPx);
      if (bgWidth > 0) {
        rects.push({
          h: wrappedRow.height,
          role: "table-header-bg",
          w: bgWidth,
          x: borderPx,
          y: rowTop,
        });
      }
      // 列ヘッダーの列境界にだけ白の縦線を引く（行の横線は引かない）。
      for (let column = 1; column < table.columnCount; column += 1) {
        const x = columnLeftPx(column);
        if (x >= innerClipRight) break;
        rects.push({
          h: wrappedRow.height,
          role: "table-header-sep",
          w: Math.min(pxScale, innerClipRight - x),
          x,
          y: rowTop,
        });
      }
    }
    for (const [column, wrappedCell] of wrappedRow.cells.entries()) {
      const cellLeft = columnLeftPx(column);
      const innerWidth = Math.max(0, (columnWidthsPx[column] ?? 0) - cellPadPx * 2);
      const contentLeft = cellLeft + cellPadPx;
      if (contentLeft >= innerClipRight) break;
      const align = table.aligns[column] ?? "left";
      let lineTop = rowTop;
      for (const [index, wrappedLine] of wrappedCell.lines.entries()) {
        const lineHeight = wrappedCell.lineHeights[index] ?? baseFontPx * STICKY_LINE_HEIGHT_RATIO;
        // ベースラインは行内で最大のフォントサイズに合わせる（部分サイズが行の高さを決める）。
        const maxFontPx = wrappedLine.reduce((max, item) => Math.max(max, item.fontPx), baseFontPx);
        const lineWidth = styledLineWidth(wrappedLine, measure);
        const startX = tableCellStartX(align, contentLeft, innerWidth, lineWidth);
        pushWrappedLineSegments(
          segments,
          clipStyledLine(wrappedLine, startX, lineWidth, innerClipRight, measure),
          startX,
          lineTop + maxFontPx,
          measure
        );
        lineTop += lineHeight;
      }
    }
    rowTop += wrappedRow.height;
  }

  // 下枠。
  rects.push({
    h: borderPx,
    role: "table-border",
    w: visibleWidthPx,
    x: 0,
    y: top + totalHeight - borderPx,
  });
  return { bottom: top + totalHeight, overflowsWidth };
}

/**
 * セルの本文を解決済みの描画属性の文字列にし、セルの内側幅で折り返す。
 * 列ヘッダーのセルは太字・白文字（run の明示があればそちらを優先する）。空セルは 0 行（高さ 0）で、
 * 行の最低高さが行の高さを決める。
 */
function wrapTableCell(
  context: BlockLayoutContext,
  cell: MarkdownTableCell,
  header: boolean,
  innerWidth: number,
  rowBackground: string
): WrappedTableCell {
  const chars: StyledChar[] = [];
  for (const span of markdownTableCellSpans(context.text, cell, context.inlineStyles)) {
    const resolved = resolveSpanStyle(span.style, span.decoration, {
      headingLine: false,
      inlineFontScale: context.inlineFontScale,
      lineBold: header || context.forceBold,
      lineFontPx: context.baseFontPx,
    });
    // 列ヘッダーの文字色は白（DOM の `thead th { color }`）。run の文字色があればそれを優先する。
    const withFinalColor = header
      ? resolved.color === undefined
        ? { ...resolved, color: MARKDOWN_TABLE_HEADER_TEXT_COLOR }
        : resolved
      : {
          ...resolved,
          color: resolveMarkdownTableForeground(
            resolved.color ??
              (span.url !== undefined
                ? context.tableBodyPalette.onDark
                  ? TEXT_LINK_COLOR_PAIRS[0]!.dark
                  : TEXT_LINK_COLOR_PAIRS[0]!.light
                : context.bodyColor),
            resolved.background ?? rowBackground
          ),
        };
    appendStyledSpanChars(chars, span, withFinalColor);
  }
  if (chars.length === 0) return { height: 0, lineHeights: [], lines: [] };
  const lines = wrapStyledChars({
    chars,
    firstLineWidth: innerWidth,
    measure: context.measure,
    wrappedWidth: innerWidth,
  });
  const lineHeights = lines.map(
    (line) =>
      // 行送りは、その行で最大のフォントサイズ（部分書式で大きい文字が混ざり得る）に合わせる。
      line.reduce((max, item) => Math.max(max, item.fontPx), context.baseFontPx) *
      STICKY_LINE_HEIGHT_RATIO
  );
  return {
    // セルの占有高さは、折り返した各行の行送りの合計。
    height: lineHeights.reduce((sum, height) => sum + height, 0),
    lineHeights,
    lines,
  };
}

/** セル内の寄せ（区切り行の `:`）に従った、本文 1 行の開始 X。 */
function tableCellStartX(
  align: MarkdownTableAlign,
  contentLeft: number,
  innerWidth: number,
  lineWidth: number
): number {
  if (align === "center") return contentLeft + Math.max(0, (innerWidth - lineWidth) / 2);
  if (align === "right") return contentLeft + Math.max(0, innerWidth - lineWidth);
  return contentLeft;
}

/**
 * 打ち切り位置（wrapper の内側右端）より右へ出る文字を落とす。列幅が使用可能幅に収まる通常の表では
 * 1 文字も落ちないため、行全体が収まる場合は 1 文字ずつの再計測を行わずそのまま返す。
 * 最小列幅の合計が使用可能幅を超える表だけが実際の切り取り対象で、DOM の `overflow: hidden` と
 * 同じ位置まで描く。
 */
function clipStyledLine(
  wrappedLine: readonly StyledChar[],
  startX: number,
  lineWidth: number,
  clipRight: number,
  measure: Measure
): readonly StyledChar[] {
  if (startX + lineWidth <= clipRight + 1e-6) return wrappedLine;
  let x = startX;
  const kept: StyledChar[] = [];
  for (const item of wrappedLine) {
    const advance = styledCharAdvance(item, measure);
    if (x + advance > clipRight + 1e-6) break;
    kept.push(item);
    x += advance;
  }
  return kept;
}

//------------------------------------------------------------------------------
// コードブロック（``` フェンス）
//------------------------------------------------------------------------------

/**
 * コードブロック 1 つをレイアウトし、面と枠線を `rects` へ、中身の行を `segments` へ積む。
 * 戻り値はブロックの下端（次のブロックの cursorY）と幅超過の有無。
 *
 * - フェンス行はセグメントも高さも出さない（DOM でも表示しない）。
 * - 中身の行は Markdown の解釈を通さず、元テキストの生の行を部分書式（run）境界だけで区切って
 *   等幅で積む（`markdownCodeBlockLines`）。リンク化・インライン装飾は起きない。
 * - 内側の余白・枠線は DOM（`.canvas-md-code` の padding / border）と同じ値。余白は em 指定のため
 *   基準フォントに比例し、枠線（画面では固定 1px）は表の外枠と同じく出力スケール（`pxScale`）で
 *   換算する。換算しないと、拡大出力で枠が細くなり本文の開始位置も画面とずれる。
 * - 寄せは align に依らず常に左（DOM の `.canvas-md-code` も `text-align: left` で固定する）。
 * - 中身が 0 行のブロックは、DOM 側の `<br>` だけの空行と同じ 1 行分の高さを確保する。
 * - 面（`code-bg`）→ 枠線（`code-border`）の順で積む。どちらもブロックの外寸と同じ矩形で、
 *   描画側が `role` を見て「塗り」と「1px の線」へ出し分ける。
 */
function layoutCodeBlock(
  context: BlockLayoutContext,
  map: MarkdownSourceMap,
  code: MarkdownCodeBlock,
  top: number,
  segments: Segment[],
  rects: LayoutRect[]
): { readonly bottom: number; readonly overflowsWidth: boolean } {
  const { baseFontPx, maxWidth, measure } = context;
  const borderPx = MARKDOWN_CODE_BORDER_PX * context.pxScale;
  const insetX = MARKDOWN_CODE_PAD_X_EM * baseFontPx + borderPx;
  const insetY = MARKDOWN_CODE_PAD_Y_EM * baseFontPx + borderPx;
  const contentWidth = maxWidth - insetX * 2;
  // 余白と枠線だけで幅を使い切る場合は、1 文字も収まらないため幅超過として扱う。
  let overflowsWidth = contentWidth <= 0;
  // 1 周目: 中身を折り返して各行の高さを確定する（面と枠はブロックの高さが要るため先に積めない）。
  const wrappedLines: { readonly chars: readonly StyledChar[]; readonly maxFontPx: number }[] = [];
  for (const line of markdownCodeBlockLines(map, code, context.inlineStyles)) {
    if (line.text.length === 0) {
      wrappedLines.push({ chars: [], maxFontPx: baseFontPx });
      continue;
    }
    const chars = codeStyledChars(context, line.pieces);
    for (const wrapped of wrapStyledChars({
      chars,
      firstLineWidth: contentWidth,
      measure,
      wrappedWidth: contentWidth,
    })) {
      if (styledLineWidth(wrapped, measure) > contentWidth) overflowsWidth = true;
      // 行送りは行内で最大のフォントサイズに合わせる（部分サイズが行高を決める）。
      const maxFontPx = wrapped.reduce((max, item) => Math.max(max, item.fontPx), baseFontPx);
      wrappedLines.push({ chars: wrapped, maxFontPx });
    }
  }
  // 中身の高さは、折り返した各行の行送りの合計。
  const contentHeight = wrappedLines.reduce(
    (sum, line) => sum + line.maxFontPx * STICKY_LINE_HEIGHT_RATIO,
    0
  );
  const blockHeight = insetY * 2 + contentHeight;
  rects.push({ h: blockHeight, role: "code-bg", w: maxWidth, x: 0, y: top });
  rects.push({ h: blockHeight, role: "code-border", w: maxWidth, x: 0, y: top });
  // 2 周目: 内側の余白の位置から左詰めで本文を積む。
  let lineTop = top + insetY;
  for (const line of wrappedLines) {
    if (line.chars.length > 0) {
      pushWrappedLineSegments(segments, line.chars, insetX, lineTop + line.maxFontPx, measure);
    }
    lineTop += line.maxFontPx * STICKY_LINE_HEIGHT_RATIO;
  }
  return { bottom: top + blockHeight, overflowsWidth };
}

/**
 * コード行の run 分割結果を、等幅の描画属性を持つ 1 文字ずつの列へ変換する。
 * 記法の装飾（太字・斜体・取消線・インラインコード）は成立しないため、run の上書きだけを反映し、
 * すべての文字へ `monospace: true` を付ける。
 */
function codeStyledChars(
  context: BlockLayoutContext,
  pieces: readonly TextInlineRawPiece[]
): readonly StyledChar[] {
  const chars: StyledChar[] = [];
  for (const piece of pieces) {
    const { style } = piece;
    const fontPx =
      style.fontSize === undefined ? context.baseFontPx : style.fontSize * context.inlineFontScale;
    const color = isValidHexColor(style.color) ? normalizeHexColor(style.color) : undefined;
    const background = resolveTextBackground(style.fill);
    for (const character of piece.text) {
      chars.push({
        // 部分太字の明示が無い区間は、図形全体の太字（forceBold）をそのまま継ぐ。
        bold: style.bold ?? context.forceBold,
        character,
        fontPx,
        monospace: true,
        ...(color !== undefined ? { color } : {}),
        ...(background !== undefined ? { background } : {}),
      });
    }
  }
  return chars;
}

type Anchor = "left" | "center" | "right";

/** 部分書式と記法の装飾を反映した 1 文字ぶんの描画属性。 */
interface StyledChar {
  readonly character: string;
  readonly fontPx: number;
  readonly bold: boolean;
  readonly italic?: boolean | undefined;
  readonly strikethrough?: boolean | undefined;
  readonly monospace?: boolean | undefined;
  readonly color?: string | undefined;
  readonly background?: string | undefined;
  /** 行内で 0 から振るコード範囲の識別子（コード範囲の外は undefined）。 */
  readonly codeRangeIndex?: number | undefined;
  /** インラインコード範囲の先頭に 1 回だけ加える左 padding（px）。 */
  readonly padBefore?: number | undefined;
  /** インラインコード範囲の末尾に 1 回だけ加える右 padding（px）。 */
  readonly padAfter?: number | undefined;
}

/** 折り返し済みの 1 行を、同じ描画属性が続く区間へまとめた結果。 */
interface StyledCharGroup {
  readonly text: string;
  readonly fontPx: number;
  readonly bold: boolean;
  readonly italic?: boolean | undefined;
  readonly strikethrough?: boolean | undefined;
  readonly monospace?: boolean | undefined;
  readonly color?: string | undefined;
  readonly background?: string | undefined;
  readonly codeRangeIndex?: number | undefined;
  readonly padBefore?: number | undefined;
  readonly padAfter?: number | undefined;
}

/** 描画属性から計測用のフォント指定を取り出す。 */
function measureFontOf(item: {
  readonly bold: boolean;
  readonly italic?: boolean | undefined;
  readonly monospace?: boolean | undefined;
}): MeasureFont {
  return {
    bold: item.bold,
    italic: item.italic === true,
    monospace: item.monospace === true,
  };
}

/** 1 文字ぶんの送り幅（実測幅 + インラインコードの padding）。 */
function styledCharAdvance(item: StyledChar, measure: Measure): number {
  return (
    (item.padBefore ?? 0) +
    measure(item.character, item.fontPx, measureFontOf(item)) +
    (item.padAfter ?? 0)
  );
}

/**
 * run の override と記法の装飾を、行のフォント・太字を既定として解決する。
 * 太字は run の明示（true / false）が最優先で、明示が無いときだけ記法の太字を採用する。
 * 斜体・取消線・等幅は run に対応項目が無いため、記法の結果をそのまま使う。
 * 文字色は run の指定があるときだけ持ち、無い区間は呼び出し側の本文色で描く（引用も同じ）。
 */
function resolveSpanStyle(
  style: TextInlineStyle,
  decoration: MarkdownInlineDecoration,
  context: {
    readonly lineFontPx: number;
    readonly lineBold: boolean;
    readonly headingLine: boolean;
    readonly inlineFontScale: number;
  }
): Omit<StyledChar, "character"> {
  const fontPx =
    style.fontSize === undefined ? context.lineFontPx : style.fontSize * context.inlineFontScale;
  // 部分太字 false は基底の太字だけを打ち消し、見出しの意味上の太字は維持する。
  const bold =
    style.bold === undefined
      ? decoration.bold || context.lineBold
      : style.bold === true
        ? true
        : context.headingLine;
  const color = isValidHexColor(style.color) ? normalizeHexColor(style.color) : undefined;
  // インラインコードの背景は、run の文字背景が無いときだけ敷く（利用者の明示指定を優先する）。
  const background =
    resolveTextBackground(style.fill) ??
    (decoration.code ? INLINE_CODE_BACKGROUND_COLOR : undefined);
  return {
    bold,
    fontPx,
    ...(decoration.italic ? { italic: true } : {}),
    ...(decoration.strikethrough ? { strikethrough: true } : {}),
    ...(decoration.code ? { monospace: true } : {}),
    ...(color !== undefined ? { color } : {}),
    ...(background !== undefined ? { background } : {}),
  };
}

/** 文字単位の詰め込み方式で折り返す（1 文字も入らない幅でもその 1 文字は必ず出力する）。 */
function wrapStyledChars(params: {
  readonly chars: readonly StyledChar[];
  readonly firstLineWidth: number;
  readonly wrappedWidth: number;
  readonly measure: Measure;
}): readonly (readonly StyledChar[])[] {
  const { chars, firstLineWidth, measure, wrappedWidth } = params;
  const lines: StyledChar[][] = [];
  let current: StyledChar[] = [];
  let currentWidth = 0;
  let availableWidth = firstLineWidth;
  for (const item of chars) {
    const width = styledCharAdvance(item, measure);
    if (currentWidth + width > availableWidth && current.length > 0) {
      lines.push(current);
      current = [item];
      currentWidth = width;
      availableWidth = wrappedWidth;
      continue;
    }
    current.push(item);
    currentWidth += width;
  }
  lines.push(current);
  return lines;
}

/**
 * 折り返し済みの 1 行を、同じ描画属性が続く区間へまとめる。
 * `codeRangeIndex` も判定へ含め、隣接する別のコード範囲が 1 つへ結合されないようにする
 * （結合すると padding と背景矩形を範囲ごとに 1 回ずつ出せない）。
 */
function groupStyledChars(chars: readonly StyledChar[]): readonly StyledCharGroup[] {
  const groups: StyledChar[][] = [];
  for (const item of chars) {
    const last = groups[groups.length - 1];
    const head = last?.[0];
    if (
      last !== undefined &&
      head !== undefined &&
      head.fontPx === item.fontPx &&
      head.bold === item.bold &&
      head.italic === item.italic &&
      head.strikethrough === item.strikethrough &&
      head.monospace === item.monospace &&
      head.color === item.color &&
      head.background === item.background &&
      head.codeRangeIndex === item.codeRangeIndex
    ) {
      last.push(item);
      continue;
    }
    groups.push([item]);
  }
  return groups.map((group) => {
    const head = group[0]!;
    // 区間の padding は、先頭の文字が持つ左 padding と、末尾の文字が持つ右 padding。
    const tail = group[group.length - 1]!;
    return {
      bold: head.bold,
      fontPx: head.fontPx,
      text: group.map((item) => item.character).join(""),
      ...(head.italic !== undefined ? { italic: head.italic } : {}),
      ...(head.strikethrough !== undefined ? { strikethrough: head.strikethrough } : {}),
      ...(head.monospace !== undefined ? { monospace: head.monospace } : {}),
      ...(head.color !== undefined ? { color: head.color } : {}),
      ...(head.background !== undefined ? { background: head.background } : {}),
      ...(head.codeRangeIndex !== undefined ? { codeRangeIndex: head.codeRangeIndex } : {}),
      ...(head.padBefore !== undefined ? { padBefore: head.padBefore } : {}),
      ...(tail.padAfter !== undefined ? { padAfter: tail.padAfter } : {}),
    };
  });
}

/** 計測・描画に使うフォントの種類（サイズ以外）。 */
interface MeasureFont {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly monospace: boolean;
}

/** 指定フォントでの文字列幅（px）を返す計測関数。Canvas 2D の measureText を包んで注入する。 */
type Measure = (text: string, fontPx: number, font: MeasureFont) => number;

interface Options {
  /** テキスト図形全体の太字。見出しの既存太字は解除しない。 */
  readonly forceBold?: boolean | undefined;
  /** 見出し・plain 行の文字寄せ（箇条書き行は常に左寄せ）。 */
  readonly align: Anchor;
  /** 基準フォントサイズ（px。スケール適用後の値を渡す）。 */
  readonly baseFontPx: number;
  /** 描画可能幅（px）。アンカー・折り返しの基準になる。 */
  readonly maxWidth: number;
  readonly measure: Measure;
  /** 生の Markdown テキスト（object.text）。 */
  readonly text: string;
  /**
   * 文字範囲だけへ適用する部分書式（正規形の run 配列）。
   * 未設定・空のときは従来どおり全文書式だけでレイアウトする。
   */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  /**
   * 部分書式の fontSize（保存値は px）へ掛けるスケール。`baseFontPx` と同じ倍率を渡す
   * （PDF は出力解像度に応じてスケールするため、run の絶対 px もそろえる必要がある）。
   * 未設定は 1 とする。
   */
  readonly inlineFontScale?: number | undefined;
  /**
   * 画面の CSS px を出力側の px へ換算する倍率（PDF・サムネイルの出力スケール）。
   * 表の外枠線（画面では固定 1px）・列ヘッダーの区切り線・コードブロックの枠線・
   * callout の左罫の固定 px 下限（`max(4px, 0.25em)` の px 側）の換算に使う。
   * 画面と同じ倍率で差し引かないと、表の使用可能幅（em）が DOM と食い違い列幅がずれる。
   * 未設定は 1（画面と同じ px）とする。
   */
  readonly pxScale?: number | undefined;
  /**
   * 本文中の表ごとの保存済み列幅（ブロック順）。渡さないと画面だけが保存幅で描かれ、
   * PDF・サムネイルが自動列幅になって列割りと折り返しが食い違う。
   */
  readonly tableColumnWidths?: TableColumnWidthsList | undefined;
  /** 表の本文偶数行へ暗背景用の縞を使うか。未指定は明背景用。 */
  readonly tableOnDark?: boolean | undefined;
  /** 表本文で実際に描画する基底文字色。 */
  readonly bodyColor?: string | undefined;
  /** 図形の塗りと本文色から解決済みの表本文配色。 */
  readonly tableBodyPalette?: MarkdownTableBodyPalette | undefined;
  /**
   * callout の背景と左罫へ暗背景用の値を使うか。未指定は明背景用。
   * 画面と同じ判定（`prefersLinkColorOnDark(object.fill)`）の結果を呼び出し側が渡す。
   */
  readonly calloutOnDark?: boolean | undefined;
}

interface Segment {
  /** ctx.textAlign へ設定する値。 */
  readonly anchor: Anchor;
  /** 描画領域の上端からのベースライン位置（px）。 */
  readonly baselineY: number;
  readonly bold: boolean;
  readonly fontPx: number;
  readonly text: string;
  /** 描画領域の左端からのアンカー X（px。インラインコードでは先頭 padding を含まない glyph 位置）。 */
  readonly x: number;
  /** 部分書式の文字色（hex）。未設定なら全文の文字色で描く。 */
  readonly color?: string | undefined;
  /** 部分書式の文字背景（hex）、またはインラインコードの背景。指定時は glyph の背後だけを塗る。 */
  readonly background?: string | undefined;
  /** 実測した描画幅（px）。文字背景の矩形を描くときだけ使う。 */
  readonly width?: number | undefined;
  /** 記法の斜体（`*斜体*`）。装飾を含まない本文では設定しない。 */
  readonly italic?: boolean | undefined;
  /** 記法の取消線（`~~取消線~~`）。装飾を含まない本文では設定しない。 */
  readonly strikethrough?: boolean | undefined;
  /** 記法のインラインコード（等幅フォント）。装飾を含まない本文では設定しない。 */
  readonly monospace?: boolean | undefined;
  /** 行内で 0 から振るコード範囲の識別子（コード範囲の外は設定しない）。 */
  readonly codeRangeIndex?: number | undefined;
  /** 背景矩形を x より左へ広げる幅（px）。インラインコード範囲の先頭にだけ付く。 */
  readonly padLeftPx?: number | undefined;
  /** 背景矩形を x + width より右へ広げる幅（px）。インラインコード範囲の末尾にだけ付く。 */
  readonly padRightPx?: number | undefined;
}

/**
 * テキスト以外の塗り矩形（水平線、引用の縦罫、表の外枠 / 列ヘッダー背景 / 列ヘッダーの区切り線）。
 * 描画順はテキストより先（背面）。
 * 座標は「描画領域の左上を原点とする矩形の左上」で、既存の `cursorY`（行の上端）と同じ基準。
 * `Segment.baselineY` のようなベースライン基準ではないことに注意する。
 * 表の縞だけは背景として先に描くため、ヘッダ背景より前に並ぶ。サムネイルは縞を読み飛ばす。
 */
interface LayoutRect {
  /** 矩形の左端（px）。 */
  readonly x: number;
  /** 矩形の**上端**（px）。中心ではない。 */
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /**
   * 塗り色の役割。呼び出し側が実際の色へ解決する（PDF とサムネイルで色系統が異なるため）。
   * - `rule`: 水平線の横罫 / `quote-bar`: 引用の左罫
   * - `table-border`: 表の外枠（上・左・右・下の 4 本） / `table-header-bg`: 列ヘッダーの背景 /
   *   `table-header-sep`: 列ヘッダーの列境界の縦線（列数 − 1 本）
   * - `code-bg`: コードブロックの面（塗り） / `code-border`: コードブロックの枠線。
   *   この 2 つは同じ矩形（ブロックの外寸）で、描画側が `role` を見て塗りと 1px の線へ出し分ける
   * - `callout-bg`: callout の背景（塗り） / `callout-accent`: callout の左罫。
   *   どちらも type ごとに色が変わるため `fill` を持つ（`role` を type ごとに分けると増えすぎる）
   */
  readonly role:
    | "rule"
    | "quote-bar"
    | "table-border"
    | "table-header-bg"
    | "table-header-sep"
    | "table-body-bg"
    | "table-row-stripe"
    | "table-row-stripe-on-dark"
    | "code-bg"
    | "code-border"
    | "callout-bg"
    | "callout-accent"
    | "task-box"
    | "task-box-checked";
  /**
   * 塗り色（CSS の色文字列）。未指定なら描画側が `role` ごとの既定色を使う。
   * `role` の union へ色を混ぜると union が `string` へ潰れ、描画側の網羅性チェックが効かなくなるため、
   * 色は別フィールドで持つ。サムネイルは抽象表現のため本フィールドを使わない。
   */
  readonly fill?: string | undefined;
}

interface Result {
  /**
   * 折り返してもいずれかのセグメントが幅へ収まらない場合 true（フィット判定用）。
   * 最小列幅の合計が使用可能幅を超える表（打ち切って描く）も true になる。
   */
  readonly overflowsWidth: boolean;
  readonly segments: readonly Segment[];
  /** 全行の行送り合計（px）。フィット判定の高さ比較に使う。表の高さ（外枠込み）も含む。 */
  readonly totalHeight: number;
  /**
   * テキスト以外の塗り矩形（水平線・引用の縦罫・表の外枠 / 列ヘッダー背景 / 区切り線）。
   * 本設計の記法を含まない本文では空で、描画処理を通らないため既存の出力は 1px も変わらない。
   */
  readonly rects: readonly LayoutRect[];
}

/**
 * 1 論理行を幅に収まるよう 1 文字単位で折り返す（日本語 / 英語混在向けの詰め込み方式。
 * 旧 wrapTextToLines と同じ判定）。1 行目とそれ以降で利用可能幅を分けられるようにし、
 * 箇条書きのぶら下げインデントを表現する。
 */
function wrapLineText(params: {
  readonly font: MeasureFont;
  readonly firstLineWidth: number;
  readonly fontPx: number;
  readonly measure: Measure;
  readonly text: string;
  readonly wrappedWidth: number;
}): string[] {
  const { font, firstLineWidth, fontPx, measure, text, wrappedWidth } = params;
  const wrapped: string[] = [];
  let current = "";
  let availableWidth = firstLineWidth;
  for (const character of text) {
    const candidate = current + character;
    if (measure(candidate, fontPx, font) > availableWidth && current.length > 0) {
      wrapped.push(current);
      current = character;
      availableWidth = wrappedWidth;
    } else {
      current = candidate;
    }
  }
  wrapped.push(current);
  return wrapped;
}
